// src/pages/BookingListPage.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import useDebounce from "../hooks/useDebounce";
import moment from "moment";

import {
  Table,
  Typography,
  Button,
  Input,
  DatePicker,
  Select,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  Spin,
  Empty,
  Drawer,
  InputNumber,
  Tooltip,
  Timeline,
} from "antd";

import {
} from "@ant-design/icons";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

import "./cs.css";
import "./BookingListPage.css";
import { AmountRows, DetailList, PageHeader, RowActions, Section, StatusTag, tablePagination, useFlatPage } from "../components/ui";
import { WarningOutlined } from "@ant-design/icons";
import { IconCard, IconExcel, IconInfo, IconPdf, IconPlus, IconRefresh, IconSearch } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_MAP = {
  0: "Pending",
  1: "Confirmed",
  2: "CheckedIn",
  3: "Cancelled",
  4: "CheckedOut",
};

const normalizeStatus = (raw) => {
  if (raw == null) return "—";
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return STATUS_MAP[n] ?? s;
  }
  return s;
};

const BookingListPage = () => {

  const { authData } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useFlatPage();

  // ─── STATE ───────────────────────────────────────────────
  const [bookings, setBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [dateRange, setDateRange] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Info modal
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Drawer
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerBooking, setDrawerBooking] = useState(null);

  // Drawer details fetched by BookingId
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerDetails, setDrawerDetails] = useState(null);
  const [drawerDetailsError, setDrawerDetailsError] = useState(null);

  // Status filter segmented/select
  const [statusFilter, setStatusFilter] = useState("all");

  // Sorting
  const [sortBy, setSortBy] = useState("checkInDate");
  const [descending, setDescending] = useState(false);

  // ─── HELPERS ─────────────────────────────────────────────
  const asNum = (v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  const toNativeDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d;
    if (typeof d?.toDate === "function") return d.toDate(); // dayjs/moment
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const toISODate = (d) => {
    const dt = toNativeDate(d);
    if (!dt) return "";
    return dt.toISOString().slice(0, 10);
  };

  const safeDiffNights = (checkIn, checkOut) => {
    const ci = toNativeDate(checkIn);
    const co = toNativeDate(checkOut);
    if (!ci || !co) return 1;
    const ms = co.getTime() - ci.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return 1;
    const nights = Math.ceil(ms / 86400000);
    return Number.isFinite(nights) && nights > 0 ? nights : 1;
  };

 // Replace your getGuestName with this:
const getGuestName = (b) => {
  if (!b) return "";

  // 1) explicit guestName on root
  const direct =
    b.guestName ??
    b.GuestName ??
    `${b.firstName ?? b.FirstName ?? ""} ${b.lastName ?? b.LastName ?? ""}`.trim();

  if (direct) return direct;

  // 2) nested guest object (this is what /Bookings/{id} returns)
  const g = b.guest ?? b.Guest ?? null;
  if (!g) return "";

  return `${g.firstName ?? g.FirstName ?? ""} ${g.lastName ?? g.LastName ?? ""}`.trim();
};

// Add this helper:
const getIdNumber = (b) => {
  if (!b) return "—";
  const g = b.guest ?? b.Guest ?? null;

  return (
    b.idNumber ??
    b.IdNumber ??
    g?.idNumber ??
    g?.IdNumber ??
    "—"
  );
};



  const getBookingId = (b) =>
    b?.bookingId ?? b?.BookingId ?? b?.id ?? b?.bookingNo ?? b?.BookingNo;

  const getBookingNumericId = (b) => {
    const n = Number(b?.bookingId ?? b?.BookingId ?? b?.id);
    return Number.isFinite(n) ? n : null;
  };

  // ✅ Default room resolver (list shows default room)
  const getRoomNumber = (b) => {
    const rn =
      b?.roomNumber ??
      b?.RoomNumber ??
      b?.roomNo ??
      b?.RoomNo ??
      b?.room?.roomNumber ??
      b?.room?.RoomNumber ??
      b?.Room?.roomNumber ??
      b?.Room?.RoomNumber ??
      b?.roomDto?.roomNumber ??
      b?.RoomDto?.RoomNumber;

    if (rn == null || String(rn).trim() === "") {
      const rid = b?.roomId ?? b?.RoomId;
      return rid ? `Room #${rid}` : "—";
    }

    return String(rn);
  };

  const getStatusText = (b) => {
    const raw =
      b?.status ??
      b?.Status ??
      b?.bookingStatus ??
      b?.BookingStatus ??
      b?.paymentStatus ??
      b?.PaymentStatus;
    return normalizeStatus(raw);
  };

  // ── Split-stay / segments (DETAILS) ──────────────────────
  // IMPORTANT FIX:
  // Your /api/Bookings/{id} returns { legs: [...] }.
  // Your drawer was calling extractSegments() which did NOT include legs,
  // therefore it always thought there were no segments.
  const extractSegments = (detailsOrBooking) => {
    const d = detailsOrBooking || {};
    return (
      d?.legs ??
      d?.Legs ??
      d?.roomSegments ??
      d?.RoomSegments ??
      d?.segments ??
      d?.Segments ??
      d?.roomBookings ??
      d?.RoomBookings ??
      d?.rooms ??
      d?.Rooms ??
      []
    );
  };

  const segRoomNumber = (seg) => {
    const rn =
      seg?.roomNumber ??
      seg?.RoomNumber ??
      seg?.room?.roomNumber ??
      seg?.room?.RoomNumber ??
      seg?.Room?.roomNumber ??
      seg?.Room?.RoomNumber;
    if (rn != null && String(rn).trim() !== "") return String(rn);
    const rid = seg?.roomId ?? seg?.RoomId ?? seg?.room?.roomId ?? seg?.Room?.RoomId;
    return rid ? `Room #${rid}` : "—";
  };

  const segRoomTypeName = (seg) => {
    return (
      seg?.roomTypeName ??
      seg?.RoomTypeName ??
      seg?.roomType?.typeName ??
      seg?.RoomType?.TypeName ??
      seg?.room?.roomType?.typeName ??
      seg?.room?.RoomType?.TypeName ??
      seg?.Room?.RoomType?.TypeName ??
      "—"
    );
  };

  const segFromDate = (seg) =>
    seg?.fromDate ??
    seg?.FromDate ??
    seg?.checkInDate ??
    seg?.CheckInDate ??
    seg?.startDate ??
    seg?.StartDate ??
    null;

  const segToDate = (seg) =>
    seg?.toDate ??
    seg?.ToDate ??
    seg?.checkOutDate ??
    seg?.CheckOutDate ??
    seg?.endDate ??
    seg?.EndDate ??
    null;

  const buildRouteString = (segments) => {
    const rooms = (segments || []).map(segRoomNumber).filter((x) => x && x !== "—");
    if (!rooms.length) return "";
    // de-dup consecutive
    const compact = rooms.filter((r, i) => i === 0 || r !== rooms[i - 1]);
    return compact.join(" → ");
  };

  const sortSegments = (segments) => {
    const list = Array.isArray(segments) ? segments : [];
    return [...list].sort((a, b) => {
      const A = toNativeDate(segFromDate(a))?.getTime?.() ?? 0;
      const B = toNativeDate(segFromDate(b))?.getTime?.() ?? 0;
      return A - B;
    });
  };

  // ── Money/price breakdown (unchanged) ─────────────────────
  const discountIsActive = (start, end, checkInDate) => {
    if (!start && !end) return true;
    const d = toNativeDate(checkInDate);
    if (!d) return true;
    const s = start ? toNativeDate(start) : null;
    const e = end ? toNativeDate(end) : null;
    return (!s || d >= s) && (!e || d <= e);
  };

  const formatMoney = (ccy, amount) => {
    const n = asNum(amount);
    const code = (ccy || "BWP").toUpperCase();
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
    return `${code} ${formatted}`;
  };

  const pricePieces = (b) => {
    const inv = b?.invoice || b?.Invoice || b?.invoiceDto || b?.InvoiceDto || {};

    const nightsFromInvoice = asNum(inv.daysBooked ?? inv.DaysBooked);
    const nights = Math.max(
      1,
      nightsFromInvoice > 0
        ? nightsFromInvoice
        : safeDiffNights(b?.checkInDate ?? b?.CheckInDate, b?.checkOutDate ?? b?.CheckOutDate)
    );

    const invTotal = asNum(inv.totalAmount ?? inv.TotalAmount ?? b?.totalAmount ?? b?.TotalAmount);
    const invPaid = asNum(inv.amountPaid ?? inv.AmountPaid ?? b?.paidAmount ?? b?.PaidAmount);

    const invBase = asNum(inv.basePrice ?? inv.BasePrice);
    const invVatPct = asNum(inv.vatPercentage ?? inv.VatPercentage);
    const invLevies = asNum(inv.additionalLevies ?? inv.AdditionalLevies);
    const invCcy = (inv.currency ?? inv.Currency ?? b?.currency ?? b?.Currency ?? "BWP").toUpperCase();

    if (invBase > 0) {
      const vatValue = invBase * (invVatPct / 100);
      const perNight = invBase + vatValue + invLevies;

      const total = invTotal > 0 ? invTotal : perNight * nights;
      const paid = invPaid;
      const due = Math.max(0, total - paid);

      return {
        nights,
        base: invBase,
        baseOriginal: invBase,
        discountPct: 0,
        discountApplied: false,
        vatPct: invVatPct,
        vat: vatValue,
        levies: invLevies,
        perNight,
        total,
        paid,
        due,
        ccy: invCcy,
        source: "invoice",
      };
    }

    const p = b?.priceInfo || b?.PriceInfo || b?.roomPrice || b?.RoomPrice || {};

    const rpBase = asNum(p.basePrice ?? p.BasePrice ?? p.price ?? p.Price ?? p.PricePerNight);
    const rpVatPct = asNum(p.vatPercentage ?? p.VatPercentage);
    const rpLevies = asNum(p.additionalLevies ?? p.AdditionalLevies);

    const rawDisc = asNum(p.discountPct ?? p.DiscountPct ?? p.discount ?? p.Discount);
    const rpStart = p.discountStart ?? p.DiscountStart;
    const rpEnd = p.discountEnd ?? p.DiscountEnd;

    const rpCcy = (p.currency ?? p.Currency ?? invCcy ?? "BWP").toUpperCase();

    if (rpBase > 0) {
      const discOn = discountIsActive(rpStart, rpEnd, b?.checkInDate ?? b?.CheckInDate);
      const isPct = rawDisc >= 0 && rawDisc <= 100;
      const discountPct = discOn && isPct ? rawDisc : 0;

      const baseEffective = rpBase * (1 - discountPct / 100);
      const vatValue = baseEffective * (rpVatPct / 100);
      const perNight = baseEffective + vatValue + rpLevies;

      const total = invTotal > 0 ? invTotal : perNight * nights;
      const paid = invPaid;
      const due = Math.max(0, total - paid);

      return {
        nights,
        base: baseEffective,
        baseOriginal: rpBase,
        discountPct,
        discountApplied: discountPct > 0,
        vatPct: rpVatPct,
        vat: vatValue,
        levies: rpLevies,
        perNight,
        total,
        paid,
        due,
        ccy: rpCcy,
        source: invTotal > 0 ? "roomprice+invoice-total" : "roomprice",
      };
    }

    const totalKnown = asNum(b?.totalAmount ?? b?.TotalAmount ?? invTotal);
    const paid = invPaid;
    const perNight = totalKnown > 0 ? Math.round((totalKnown / nights) * 100) / 100 : 0;
    const due = Math.max(0, totalKnown - paid);

    return {
      nights,
      base: 0,
      baseOriginal: 0,
      discountPct: 0,
      discountApplied: false,
      vatPct: 0,
      vat: 0,
      levies: 0,
      perNight,
      total: totalKnown,
      paid,
      due,
      ccy: invCcy || "BWP",
      source: "derived",
    };
  };

  // ─── FETCH LIST ───────────────────────────────────────────
  const fetchBookings = useCallback(
    async (hotelId) => {
      if (!hotelId) return [];

      setLoading(true);
      try {
        const res = await axiosInstance.get(`/Bookings/all/${hotelId}`, {
          params: {
            sortBy: "checkInDate",
            descending: false,
            from: dateFrom || undefined,
            to: dateTo || undefined,
          },
          validateStatus: (s) => [200, 204, 404].includes(s),
        });

        const list = res.status === 200 ? res.data || [] : [];
        setBookings(list);
        setError(null);
        return list;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching bookings:", err);
        setError("Failed to fetch bookings. Please try again later.");
        notify.error({
          title: "Error",
          description: "Failed to fetch bookings. Please try again later.",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo]
  );

  useEffect(() => {
    const hid = authData?.hotelId;
    if (hid) fetchBookings(hid);
    else {
      setError("User authentication is missing. Please log in.");
      navigate("/login");
    }
  }, [authData?.hotelId, fetchBookings, navigate]);

  // ─── FETCH DETAILS ────────────────────────────────────────
  const fetchBookingDetailsById = useCallback(
    async (bookingId) => {
      const id = Number(bookingId);
      if (!Number.isFinite(id) || id <= 0) return null;

      setDrawerLoading(true);
      setDrawerDetailsError(null);

      try {
        const res = await axiosInstance.get(`/Bookings/${id}`, {
          validateStatus: (s) => [200, 404].includes(s),
        });

        if (res.status === 404) {
          setDrawerDetails(null);
          setDrawerDetailsError("Booking details not found.");
          return null;
        }

        const data = res.data ?? null;

        // Normalize legs key if API returns "Legs" vs "legs"
        if (data) {
          const legs = data.legs ?? data.Legs ?? [];
          data.legs = Array.isArray(legs) ? legs : [];

          // normalize rooms meta too (optional)
          if (data.roomsRoute == null && data.RoomsRoute != null) data.roomsRoute = data.RoomsRoute;
          if (data.roomsSummary == null && data.RoomsSummary != null) data.roomsSummary = data.RoomsSummary;
          if (data.roomsCount == null && data.RoomsCount != null) data.roomsCount = data.RoomsCount;
          if (data.isSplitStay == null && data.IsSplitStay != null) data.isSplitStay = data.IsSplitStay;
        }

        setDrawerDetails(data);
        return data;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching booking details:", err);

        setDrawerDetails(null);
        setDrawerDetailsError("Failed to load booking details.");
        notify.error({ title: "Error", description: "Failed to load booking details." });

        return null;
      } finally {
        setDrawerLoading(false);
      }
    },
    []
  );

  const openDrawer = async (b) => {
    setDrawerBooking(b);
    setDrawerDetails(null);
    setDrawerDetailsError(null);
    setShowDrawer(true);

    const id = getBookingNumericId(b);
    if (!id) return;

    await fetchBookingDetailsById(id);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setDrawerBooking(null);
    setDrawerDetails(null);
    setDrawerDetailsError(null);
    setDrawerLoading(false);
  };

  // ─── STATUS COUNTS / SEGMENTED ────────────────────────────
  const statusCounts = useMemo(() => {
    const counts = {
      all: bookings.length,
      Confirmed: 0,
      Pending: 0,
      Cancelled: 0,
      CheckedIn: 0,
      CheckedOut: 0,
    };
    bookings.forEach((b) => {
      const s = getStatusText(b);
      if (counts[s] != null) counts[s] += 1;
    });
    return counts;
  }, [bookings]);

  const statusTabs = useMemo(
    () => [
      { key: "all", label: "All", count: statusCounts.all },
      { key: "Confirmed", label: "Confirmed", count: statusCounts.Confirmed },
      { key: "Pending", label: "Pending", count: statusCounts.Pending },
      { key: "CheckedIn", label: "Checked in", count: statusCounts.CheckedIn },
      { key: "CheckedOut", label: "Checked out", count: statusCounts.CheckedOut },
      { key: "Cancelled", label: "Cancelled", count: statusCounts.Cancelled },
    ],
    [statusCounts]
  );

  // ─── FILTERING ────────────────────────────────────────────
  const filteredBookings = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();

    return bookings.filter((b) => {
      const guestName = (getGuestName(b) || "").toLowerCase();
      const roomTypeName = (b?.roomTypeName ?? b?.RoomTypeName ?? "").toLowerCase();
      const roomNo = String(getRoomNumber(b) || "").toLowerCase();
      const idNumber = String(b?.idNumber ?? b?.IdNumber ?? "").toLowerCase();
      const bookingNo = String(b?.bookingNo ?? b?.BookingNo ?? "").toLowerCase();

      const textMatch =
        !q ||
        guestName.includes(q) ||
        roomTypeName.includes(q) ||
        roomNo.includes(q) ||
        idNumber.includes(q) ||
        bookingNo.includes(q);

      const ciISO = toISODate(b?.checkInDate ?? b?.CheckInDate);
      const dateOk =
        (!dateFrom || (ciISO && ciISO >= dateFrom)) &&
        (!dateTo || (ciISO && ciISO <= dateTo));

      const statusOk = statusFilter === "all" ? true : getStatusText(b) === statusFilter;

      return textMatch && dateOk && statusOk;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toISODate is a pure helper defined inside the component
  }, [bookings, debouncedSearchQuery, dateFrom, dateTo, statusFilter]);

  // ─── SORTING ──────────────────────────────────────────────
  const sortedBookings = useMemo(() => {
    const getSortValue = (b) => {
      if (sortBy === "amount") return pricePieces(b).total;
      if (sortBy === "guestName") return (getGuestName(b) || "").toLowerCase();
      if (sortBy === "roomTypeName") return (b?.roomTypeName ?? b?.RoomTypeName ?? "").toLowerCase();

      if (sortBy === "roomNumber") {
        const rn = getRoomNumber(b);
        const match = String(rn ?? "").match(/\d+/);
        const num = match ? parseInt(match[0], 10) : null;
        return num !== null ? num : String(rn ?? "").toLowerCase();
      }

      if (sortBy === "status") return getStatusText(b);

      if (["checkInDate", "checkOutDate", "bookingDate"].includes(sortBy)) {
        const v = b?.[sortBy] ?? b?.[sortBy[0].toUpperCase() + sortBy.slice(1)];
        const dt = toNativeDate(v) || new Date(0);
        return dt.getTime();
      }

      return b?.[sortBy];
    };

    return [...filteredBookings].sort((a, b) => {
      const A = getSortValue(a);
      const B = getSortValue(b);

      if (A < B) return descending ? 1 : -1;
      if (A > B) return descending ? -1 : 1;
      return 0;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pricePieces is a pure helper defined inside the component
  }, [filteredBookings, sortBy, descending]);

  // ─── PAGINATION ───────────────────────────────────────────
  const displayedBookings = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedBookings.slice(start, start + itemsPerPage);
  }, [sortedBookings, currentPage, itemsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredBookings.length / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredBookings.length, itemsPerPage, currentPage]);

  const handleSort = (col) => {
    if (sortBy === col) setDescending((d) => !d);
    else {
      setSortBy(col);
      setDescending(false);
    }
    setCurrentPage(1);
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    setDateRange(dates);
    setDateFrom(dateStrings?.[0] || "");
    setDateTo(dateStrings?.[1] || "");
    setCurrentPage(1);
  };

  // ─── EXPORT HELPERS ───────────────────────────────────────
  const buildExportRows = (rows) =>
    rows.map((b) => {
      const p = pricePieces(b);
      return {
        Guest: getGuestName(b) || "—",
        "ID Number": b?.idNumber ?? b?.IdNumber ?? "—",
        "Room No": getRoomNumber(b) || "—",
        "Room Type": b?.roomTypeName ?? b?.RoomTypeName ?? "—",
        "Check-In": (b?.checkInDate ?? b?.CheckInDate)
          ? new Date(b?.checkInDate ?? b?.CheckInDate).toLocaleDateString()
          : "",
        "Check-Out": (b?.checkOutDate ?? b?.CheckOutDate)
          ? new Date(b?.checkOutDate ?? b?.CheckOutDate).toLocaleDateString()
          : "",
        "Per Night": p.perNight,
        Nights: p.nights,
        Total: p.total,
        Paid: p.paid,
        Due: p.due,
        Status: getStatusText(b),
        "Booking No": b?.bookingNo ?? b?.BookingNo ?? "—",
        "Room ID": b?.roomId ?? b?.RoomId ?? "—",
      };
    });

  const handleExportExcel = () => {
    const rows = buildExportRows(filteredBookings);
    if (!rows.length) {
      notify.warning({
        title: "No data to export",
        description: "Adjust your filters or date range and try again.",
      });
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `BookingReport_${Date.now()}.xlsx`
    );
    notify.success({ title: "Export Successful", description: "Booking report exported to Excel." });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows(filteredBookings);
    if (!rows.length) {
      notify.warning({
        title: "No data to export",
        description: "Adjust your filters or date range and try again.",
      });
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    doc.text("Booking Report", 14, 10);

    let subtitle = "";
    if (dateFrom || dateTo) subtitle += `Period: ${dateFrom || "..."} - ${dateTo || "..."}`;
    if (statusFilter !== "all") subtitle += subtitle ? ` | Status: ${statusFilter}` : `Status: ${statusFilter}`;

    if (subtitle) {
      doc.setFontSize(10);
      doc.text(subtitle, 14, 16);
      doc.setFontSize(12);
    }

    doc.autoTable({
      startY: subtitle ? 20 : 14,
      head: [[
        "Guest",
        "ID Number",
        "Room No",
        "Room Type",
        "Check-In",
        "Check-Out",
        "Per Night",
        "Nights",
        "Total",
        "Paid",
        "Due",
        "Status",
      ]],
      body: rows.map((r) => [
        r["Guest"],
        r["ID Number"],
        r["Room No"],
        r["Room Type"],
        r["Check-In"],
        r["Check-Out"],
        r["Per Night"],
        r["Nights"],
        r["Total"],
        r["Paid"],
        r["Due"],
        r["Status"],
      ]),
    });

    doc.save(`BookingReport_${Date.now()}.pdf`);
    notify.success({ title: "Export Successful", description: "Booking report exported to PDF." });
  };

  // ─── EDIT / CANCEL / PAYMENT (UNCHANGED) ──────────────────
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedBooking(null);
    form.resetFields();
  };

  const handleEditClick = (b) => {
    const status = getStatusText(b);
    if (status !== "Confirmed") {
      notify.warning({
        title: "Cannot Edit",
        description: "This booking is not in a state that allows editing.",
      });
      return;
    }

    const today = new Date();
    const ci = toNativeDate(b?.checkInDate ?? b?.CheckInDate);
    today.setHours(0, 0, 0, 0);

    if (ci) {
      ci.setHours(0, 0, 0, 0);
      if (today > ci) {
        notify.warning({ title: "Cannot Edit", description: "Booking cannot be edited after check-in." });
        return;
      }
    }

    setSelectedBooking(b);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (values) => {
    try {
      const id = selectedBooking?.bookingId ?? selectedBooking?.BookingId;
      if (!id) {
        notify.error({ title: "Error", description: "No booking selected." });
        return;
      }

      const payload = {
        bookingId: id,
        bookingNo: selectedBooking.bookingNo ?? selectedBooking.BookingNo,
        hotelId: selectedBooking.hotelId ?? selectedBooking.HotelId,
        guestId: selectedBooking.guestId ?? selectedBooking.GuestId,
        roomId: selectedBooking.roomId ?? selectedBooking.RoomId,
        roomTypeId: selectedBooking.roomTypeId ?? selectedBooking.RoomTypeId,

        checkInDate: values.checkInDate?.format?.("YYYY-MM-DD"),
        checkOutDate: values.checkOutDate?.format?.("YYYY-MM-DD"),

        totalAmount: Number(selectedBooking.totalAmount ?? selectedBooking.TotalAmount ?? 0),
        paidAmount: Number(selectedBooking.paidAmount ?? selectedBooking.PaidAmount ?? 0),
        discount: Number(selectedBooking.discount ?? selectedBooking.Discount ?? 0),

        paymentStatus: selectedBooking.paymentStatus ?? selectedBooking.PaymentStatus,
        status: selectedBooking.status ?? selectedBooking.Status,

        remarks: (values.remarks ?? "").trim() || "-",
        notes: values.notes ?? selectedBooking.notes ?? selectedBooking.Notes ?? "",

        idNumber: selectedBooking.idNumber ?? selectedBooking.IdNumber,
        firstName: selectedBooking.firstName ?? selectedBooking.FirstName,
        lastName: selectedBooking.lastName ?? selectedBooking.LastName,
        email: selectedBooking.email ?? selectedBooking.Email,
        mobileNumber: selectedBooking.mobileNumber ?? selectedBooking.MobileNumber ?? "",
        phoneNumber: selectedBooking.phoneNumber ?? selectedBooking.PhoneNumber ?? "",
        address: selectedBooking.address ?? selectedBooking.Address ?? "",
        dateOfBirth: selectedBooking.dateOfBirth ?? selectedBooking.DateOfBirth,

        numberOfAdults: selectedBooking.numberOfAdults ?? selectedBooking.NumberOfAdults ?? 1,
        numberOfKids: selectedBooking.numberOfKids ?? selectedBooking.NumberOfKids ?? 0,
        includesKids: selectedBooking.includesKids ?? selectedBooking.IncludesKids ?? false,
      };

      await axiosInstance.put(`/Bookings/${id}`, payload);

      notify.success({ title: "Saved", description: "Booking updated successfully." });

      if (authData?.hotelId) await fetchBookings(authData.hotelId);
      closeEditModal();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error updating booking:", err);
      const api = err?.response?.data;

      if (api?.errors) {
        const flat = Object.entries(api.errors)
          .map(([k, arr]) => `${k}: ${arr.join(", ")}`)
          .join("\n");
        notify.error({ title: api.title || "Validation error", description: flat });
      } else {
        notify.error({ title: "Error", description: api?.message || "Failed to update booking." });
      }
    }
  };

  const handleCancelBooking = async (bookingOrId) => {
    try {
      const id =
        typeof bookingOrId === "object"
          ? bookingOrId.bookingId ?? bookingOrId.BookingId
          : bookingOrId;

      if (!id) {
        notify.error({ title: "Error", description: "No booking selected." });
        return;
      }

      const status = typeof bookingOrId === "object" ? getStatusText(bookingOrId) : null;
      if (status && !["Pending", "Confirmed"].includes(status)) {
        notify.warning({
          title: "Not Allowed",
          description: `You can only cancel Pending/Confirmed bookings. Current status: ${status}.`,
        });
        return;
      }

      const res = await axiosInstance.post(
        `/Bookings/${id}/cancel`,
        { reason: "Cancelled from booking list" },
        { validateStatus: (s) => s < 500 }
      );

      if (res.status >= 200 && res.status < 300) {
        notify.success({ title: "Cancelled", description: "Booking cancelled successfully." });
        if (authData?.hotelId) await fetchBookings(authData.hotelId);
        closeEditModal();
        return;
      }

      notify.error({
        title: "Error",
        description: res?.data?.message || "Failed to cancel booking.",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error cancelling booking:", err);
      notify.error({
        title: "Error",
        description: err?.response?.data?.message || "Failed to cancel booking.",
      });
    }
  };

  const handleHardDeleteBooking = async (bookingOrId) => {
    try {
      const id =
        typeof bookingOrId === "object"
          ? bookingOrId.bookingId ?? bookingOrId.BookingId
          : bookingOrId;

      if (!id) {
        notify.error({ title: "Error", description: "No booking selected." });
        return;
      }

      const status = typeof bookingOrId === "object" ? getStatusText(bookingOrId) : null;
      if (status && !["Pending", "Cancelled"].includes(status)) {
        notify.warning({
          title: "Not Allowed",
          description: `Hard delete is only allowed for Pending/Cancelled bookings. Current status: ${status}.`,
        });
        return;
      }

      const res = await axiosInstance.delete(`/Bookings/${id}`, {
        validateStatus: (s) => s < 500,
      });

      if (res.status >= 200 && res.status < 300) {
        notify.success({ title: "Deleted", description: "Booking deleted successfully." });
        if (authData?.hotelId) await fetchBookings(authData.hotelId);
        return;
      }

      notify.error({ title: "Error", description: res?.data?.message || "Failed to delete booking." });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error deleting booking:", err);
      notify.error({
        title: "Error",
        description: err?.response?.data?.message || "Failed to delete booking.",
      });
    }
  };

  const getStatusTag = (status) => {
    const s = status == null ? "—" : String(status);
    const labels = { CheckedIn: "Checked in", CheckedOut: "Checked out" };
    return <StatusTag status={s}>{labels[s] || s}</StatusTag>;
  };

  const recordPaymentForBooking = async (booking, detailsValues, amount, method, reference, notes, currency) => {
    const id = booking.bookingId ?? booking.BookingId;
    const hotelId = booking.hotelId ?? booking.HotelId ?? authData?.hotelId;

    try {
      const res = await axiosInstance.post(
        `/Bookings/${id}/payments`,
        {
          hotelId,
          bookingId: id,
          amount: Number(amount),
          currency,
          method,
          reference,
          notes,
        },
        { validateStatus: (s) => [200, 201, 204, 400, 404, 405].includes(s) }
      );
      if ([200, 201, 204].includes(res.status)) return true;
      if (res.status === 400) throw new Error(res.data?.message || "Payment validation failed.");
    } catch {
      // fall back to update booking with bumped paid amount
    }

    const bump = Number(booking.paidAmount ?? booking.PaidAmount ?? 0) + Number(amount);

    const payload = {
      bookingId: id,
      bookingNo: booking.bookingNo ?? booking.BookingNo,
      hotelId,
      guestId: booking.guestId ?? booking.GuestId,
      roomId: booking.roomId ?? booking.RoomId,
      roomTypeId: booking.roomTypeId ?? booking.RoomTypeId,

      checkInDate: detailsValues.checkInDate?.format?.("YYYY-MM-DD"),
      checkOutDate: detailsValues.checkOutDate?.format?.("YYYY-MM-DD"),

      totalAmount: Number(booking.totalAmount ?? booking.TotalAmount ?? 0),
      paidAmount: bump,
      discount: Number(booking.discount ?? booking.Discount ?? 0),

      paymentStatus: booking.paymentStatus ?? booking.PaymentStatus,
      status: booking.status ?? booking.Status,

      remarks: (detailsValues.remarks ?? booking.remarks ?? booking.Remarks ?? "") || "-",
      notes: detailsValues.notes ?? booking.notes ?? booking.Notes ?? "",

      idNumber: booking.idNumber ?? booking.IdNumber,
      firstName: booking.firstName ?? booking.FirstName,
      lastName: booking.lastName ?? booking.LastName,
      email: booking.email ?? booking.Email,
      mobileNumber: booking.mobileNumber ?? booking.MobileNumber ?? "",
      phoneNumber: booking.phoneNumber ?? booking.PhoneNumber ?? "",
      address: booking.address ?? booking.Address ?? "",
      dateOfBirth: booking.dateOfBirth ?? booking.DateOfBirth,

      numberOfAdults: booking.numberOfAdults ?? booking.NumberOfAdults ?? 1,
      numberOfKids: booking.numberOfKids ?? booking.NumberOfKids ?? 0,
      includesKids: booking.includesKids ?? booking.IncludesKids ?? false,
    };

    const res2 = await axiosInstance.put(`/Bookings/${id}`, payload, { validateStatus: (s) => s < 500 });
    if (res2.status >= 200 && res2.status < 300) return true;

    throw new Error(res2.data?.message || "Failed to update booking with payment.");
  };

  // ─── TABLE COLUMNS ────────────────────────────────────────
  const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const columns = [
    {
      title: "Guest",
      dataIndex: "guestName",
      key: "guestName",
      width: 230,
      render: (_, record) => {
        const name = getGuestName(record) || "Unknown";
        return (
          <div className="bl-guest">
            <span className="bl-guest-name" title={name}>
              {name}
            </span>
            <span className="bl-sub">#{getBookingId(record) ?? "—"}</span>
          </div>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("guestName") }),
    },
    {
      title: "Room",
      key: "roomNumber",
      width: 130,
      render: (_, record) => {
        const defaultRoom = getRoomNumber(record);
        const hintedCount = Number(record?.roomsCount ?? record?.RoomsCount);
        const splitHint = Number.isFinite(hintedCount) && hintedCount > 1;
        const tip = splitHint
          ? `Default: ${defaultRoom}\nSplit stay: Yes (${hintedCount} rooms)\nOpen View to see move plan.`
          : `Default: ${defaultRoom}`;

        return (
          <Tooltip title={<div style={{ whiteSpace: "pre-wrap" }}>{tip}</div>}>
            <span className="bl-room">
              {defaultRoom}
              {splitHint && <span className="bl-pill">{hintedCount} rooms</span>}
            </span>
          </Tooltip>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("roomNumber") }),
    },
    {
      title: "Room type",
      dataIndex: "roomTypeName",
      key: "roomTypeName",
      width: 170,
      render: (text, record) => text ?? record?.RoomTypeName ?? "Unknown",
      onHeaderCell: () => ({ onClick: () => handleSort("roomTypeName") }),
    },
    {
      title: "Check-in",
      dataIndex: "checkInDate",
      key: "checkInDate",
      width: 120,
      render: (text, record) => fmtDate(text ?? record?.CheckInDate),
      onHeaderCell: () => ({ onClick: () => handleSort("checkInDate") }),
    },
    {
      title: "Check-out",
      dataIndex: "checkOutDate",
      key: "checkOutDate",
      width: 140,
      render: (text, record) => {
        const out = text ?? record?.CheckOutDate;
        const inn = record?.checkInDate ?? record?.CheckInDate;
        const bad = out && inn && new Date(out) < new Date(inn);
        return (
          <span className={bad ? "bl-warn" : undefined}>
            {fmtDate(out)}
            {bad && (
              <Tooltip title="Check-out is before check-in. Open the booking and correct the dates.">
                <WarningOutlined style={{ marginLeft: 6 }} />
              </Tooltip>
            )}
          </span>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("checkOutDate") }),
    },
    {
      title: "Amount",
      key: "amount",
      width: 150,
      align: "right",
      render: (_, record) => {
        const p = pricePieces(record);
        return (
          <div className="bl-amount">
            <span className="bl-amount-total">{formatMoney(p.ccy, p.total)}</span>
            <span className="bl-sub">{formatMoney(p.ccy, p.perNight)}/night</span>
          </div>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("amount") }),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "status",
      width: 130,
      render: (_, record) => getStatusTag(getStatusText(record)),
      onHeaderCell: () => ({ onClick: () => handleSort("status") }),
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 140,
      align: "right",
      render: (_, record) => {
        const status = getStatusText(record);
        const canCancel = ["Pending", "Confirmed"].includes(status);
        const canHardDelete = ["Pending", "Cancelled"].includes(status);

        return (
          <RowActions
            actions={[
              { key: "view", label: "View", icon: "view", onClick: () => openDrawer(record) },
              { key: "edit", label: "Edit booking", icon: "edit", onClick: () => handleEditClick(record) },
              {
                key: "cancel",
                label: "Cancel booking",
                icon: "close",
                danger: true,
                hidden: !canCancel,
                keepSpace: true,
                confirm: "Cancel this booking? The history is kept.",
                okText: "Yes, cancel",
                onClick: () => handleCancelBooking(record),
              },
            ]}
            more={[
              {
                key: "hard-delete",
                label: "Delete permanently",
                danger: true,
                hidden: !canHardDelete,
                confirm: "Delete this booking permanently?",
                confirmDetail: "This removes the booking record and cannot be undone.",
                okText: "Delete",
                onClick: () => handleHardDeleteBooking(record),
              },
            ]}
          />
        );
      },
    },
  ];

  // ─── RENDER ──────────────────────────────────────────────
  return (
    <div className="hms-page bl-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconExcel />} onClick={handleExportExcel}>
              Excel
            </Button>
            <Button icon={<IconPdf />} onClick={handleExportPdf}>
              PDF
            </Button>
            <Button icon={<IconRefresh />} onClick={() => authData?.hotelId && fetchBookings(authData.hotelId)}>
              Refresh
            </Button>
            <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => navigate("/hotels/bookingpage")}>
              New booking
            </Button>
          </>
        }
      />

      <div className="hms-card is-flush">
        <div className="bl-tabs" role="tablist">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`bl-tab ${statusFilter === tab.key ? "is-active" : ""}`}
              onClick={() => {
                setStatusFilter(tab.key);
                setCurrentPage(1);
              }}
            >
              {tab.label}
              <span className="bl-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="bl-filters">
          <Input
            className="bl-search"
            prefix={<IconSearch />}
            placeholder="Search guest, room, booking no or ID number"
            allowClear
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          <RangePicker onChange={handleDateRangeChange} value={dateRange} />
          {(searchQuery || (dateRange && dateRange.length > 0)) && (
            <Button
              type="link"
              onClick={() => {
                setSearchQuery("");
                setDateRange([]);
                setDateFrom("");
                setDateTo("");
                setCurrentPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ margin: 16 }}
            action={
              <Button size="small" type="primary" onClick={() => authData?.hotelId && fetchBookings(authData.hotelId)}>
                Retry
              </Button>
            }
          />
        )}

        <Table
          className="hms-table bl-table"
          rowKey={(r) =>
            getBookingNumericId(r) ??
            getBookingId(r) ??
            `${r?.roomId ?? "x"}-${r?.checkInDate ?? r?.CheckInDate ?? "0"}`
          }
          loading={loading}
          dataSource={displayedBookings}
          columns={columns}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: loading ? <Spin /> : <Empty description="No bookings found" />,
          }}
          pagination={tablePagination("booking", {
            current: currentPage,
            pageSize: itemsPerPage,
            total: filteredBookings.length,
            onChange: (page, size) => {
              if (size !== itemsPerPage) {
                setItemsPerPage(size);
                setCurrentPage(1);
              } else {
                setCurrentPage(page);
              }
            },
          })}
        />
      </div>

      {/* Edit Booking Modal (unchanged from your version) */}
      {showEditModal &&
        selectedBooking &&
        (() => {
          const p = pricePieces(selectedBooking);
          const due = Math.max(0, p?.due ?? 0);
          const ccy = p?.ccy ?? "BWP";

          return (
            <Modal
              title="Edit booking"
              open={showEditModal}
              onCancel={closeEditModal}
              footer={null}
              width={720}
              centered
              destroyOnHidden
            >
              <Form
                form={form}
                initialValues={{
                  checkInDate: selectedBooking.checkInDate
                    ? moment(selectedBooking.checkInDate)
                    : selectedBooking.CheckInDate
                    ? moment(selectedBooking.CheckInDate)
                    : null,
                  checkOutDate: selectedBooking.checkOutDate
                    ? moment(selectedBooking.checkOutDate)
                    : selectedBooking.CheckOutDate
                    ? moment(selectedBooking.CheckOutDate)
                    : null,
                  remarks: selectedBooking.remarks ?? selectedBooking.Remarks ?? "-",
                  notes: selectedBooking.notes ?? selectedBooking.Notes ?? "",
                }}
                onFinish={handleSaveEdit}
                layout="vertical"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="checkInDate"
                      label="Check-In Date"
                      rules={[{ required: true, message: "Please select check-in date" }]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="checkOutDate"
                      label="Check-Out Date"
                      dependencies={["checkInDate"]}
                      rules={[
                        { required: true, message: "Please select check-out date" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const ci = getFieldValue("checkInDate");
                            if (!value || !ci || value.isAfter(ci, "day")) return Promise.resolve();
                            return Promise.reject(new Error("Check-out must be after check-in"));
                          },
                        }),
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="remarks" label="Remarks" rules={[{ required: true, message: "Remarks are required" }]}>
                  <Input.TextArea rows={2} placeholder="Reason / context for the change" />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={2} placeholder="Internal notes (optional)" />
                </Form.Item>

                <div className="hms-form-actions">
                  <Button onClick={closeEditModal}>Cancel</Button>
                  <Button type="primary" htmlType="submit">
                    Save changes
                  </Button>
                </div>
              </Form>

              <Section title="Payment">
                <AmountRows
                  rows={[
                    { label: "Total", value: formatMoney(ccy, p?.total ?? 0), strong: true },
                    { label: "Paid", value: formatMoney(ccy, p?.paid ?? 0), muted: true },
                    { label: "Due", value: formatMoney(ccy, due), danger: true },
                  ]}
                />

                <Form
                  layout="vertical"
                  onFinish={async (vals) => {
                    try {
                      const amt = Number(vals.payAmount || 0);
                      if (!(amt > 0)) {
                        notify.warning({ title: "Warning", description: "Enter a payment amount > 0" });
                        return;
                      }
                      if (amt > due + 0.01) {
                        notify.warning({
                          title: "Warning",
                          description: `Amount exceeds due (${formatMoney(ccy, due)})`,
                        });
                        return;
                      }

                      const detailsValues = form.getFieldsValue();

                      await recordPaymentForBooking(
                        selectedBooking,
                        detailsValues,
                        amt,
                        vals.payMethod,
                        vals.payRef,
                        vals.payNotes,
                        ccy
                      );

                      notify.success({ title: "Success", description: "Payment recorded" });

                      if (authData?.hotelId) {
                        const list = await fetchBookings(authData.hotelId);
                        const refreshed = list.find((x) => getBookingId(x) === getBookingId(selectedBooking));
                        if (refreshed) setSelectedBooking(refreshed);
                      }
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error(err);
                      notify.error({ title: "Payment failed", description: err?.message || "Please try again." });
                    }
                  }}
                  initialValues={{
                    payAmount: due > 0 ? Number(due.toFixed(2)) : 0,
                    payMethod: "Cash",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="payAmount"
                        label={`Amount (${ccy})`}
                        rules={[{ required: true, message: "Enter amount" }]}
                      >
                        <InputNumber min={0.01} precision={2} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name="payMethod" label="Method" rules={[{ required: true }]}>
                        <Select
                          options={[
                            { value: "Cash", label: "Cash" },
                            { value: "Card", label: "Card" },
                            { value: "BankTransfer", label: "Bank Transfer" },
                            { value: "MobileMoney", label: "Mobile Money" },
                            { value: "Online", label: "Online" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="payRef" label="Reference (optional)">
                    <Input placeholder="Receipt no / POS / Txn id" />
                  </Form.Item>

                  <Form.Item name="payNotes" label="Notes (optional)">
                    <Input.TextArea rows={2} placeholder="Any remarks" />
                  </Form.Item>

                  <div className="hms-form-actions">
                    <Button type="primary" htmlType="submit" icon={<IconCard />} disabled={due <= 0}>
                      Record payment
                    </Button>
                  </div>
                </Form>
              </Section>
            </Modal>
          );
        })()}

      {/* Info Modal */}
      <Modal
        title="Booking management guide"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
        width={560}
        centered
        destroyOnHidden
      >
        <p className="hms-modal-note">This module lets you manage all bookings for your hotel.</p>

        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>View all current and upcoming bookings</li>
            <li>Edit booking dates (before check-in)</li>
            <li>Cancel bookings when needed</li>
            <li>Filter and search bookings easily</li>
            <li>Export booking data to Excel or PDF</li>
          </ul>
        </Section>

        <Section title="Right now">
          <DetailList
            items={[
              { label: "Total bookings", value: bookings.length },
              { label: "Last updated", value: new Date().toLocaleString("en-GB") },
            ]}
          />
        </Section>
      </Modal>

      {/* Booking Details Drawer (UPDATED: shows legs from /Bookings/{id}) */}
      <Drawer
        title="Booking details"
        open={showDrawer}
        // AntD v6: Drawer "size" expects "default" | "large" (not a number)
        size="large"
        onClose={closeDrawer}
        destroyOnHidden
      >
        {drawerDetailsError && (
          <Alert
            type="warning"
            showIcon
            message="Details not available"
            description={drawerDetailsError}
            style={{ marginBottom: 12 }}
            action={
              <Button
                size="small"
                onClick={() => {
                  const id = getBookingNumericId(drawerBooking);
                  if (id) fetchBookingDetailsById(id);
                }}
              >
                Retry
              </Button>
            }
          />
        )}

        {/* While details are fetching, show a clear loader instead of "No segments" */}
        {drawerLoading && !drawerDetails ? (
          <div style={{ padding: 18, display: "flex", justifyContent: "center" }}>
            <Spin />
          </div>
        ) : (drawerDetails || drawerBooking) ? (
          (() => {
            const base = drawerDetails || drawerBooking;
            const p = pricePieces(base);
            const statusText = getStatusText(base);

            const segmentsRaw = extractSegments(base);
            const segments = sortSegments(segmentsRaw);

            const apiSplit = Boolean(base?.isSplitStay ?? base?.IsSplitStay);
            const split = apiSplit || segments.length > 1;

            const apiRoute = String(base?.roomsRoute ?? base?.RoomsRoute ?? "").trim();
            const apiSummary = String(base?.roomsSummary ?? base?.RoomsSummary ?? "").trim();
            const route = apiRoute || buildRouteString(segments);

            // Default room can be in API as Room/DefaultRoom. Fallback to list.
            const defaultRoomObj = base?.defaultRoom ?? base?.DefaultRoom ?? base?.room ?? base?.Room ?? null;
            const defaultRoomNumber =
              defaultRoomObj?.roomNumber ??
              defaultRoomObj?.RoomNumber ??
              getRoomNumber(drawerBooking);

            const defaultRoomType =
              defaultRoomObj?.roomType?.typeName ??
              defaultRoomObj?.RoomType?.TypeName ??
              defaultRoomObj?.roomTypeName ??
              defaultRoomObj?.RoomTypeName ??
              base?.roomTypeName ??
              base?.RoomTypeName ??
              "—";

            const segColumns = [
              {
                title: "#",
                key: "seq",
                width: 48,
                render: (_, __, idx) => <Text type="secondary">{idx + 1}</Text>,
              },
              {
                title: "Room",
                key: "room",
                width: 110,
                render: (_, seg) => <strong>{segRoomNumber(seg)}</strong>,
              },
              {
                title: "Room Type",
                key: "type",
                width: 170,
                render: (_, seg) => <span title={segRoomTypeName(seg)}>{segRoomTypeName(seg)}</span>,
              },
              {
                title: "From",
                key: "from",
                width: 120,
                render: (_, seg) => {
                  const d = segFromDate(seg);
                  return fmtDate(d);
                },
              },
              {
                title: "To",
                key: "to",
                width: 120,
                render: (_, seg) => {
                  const d = segToDate(seg);
                  return fmtDate(d);
                },
              },
              {
                title: "Nights",
                key: "nights",
                width: 90,
                render: (_, seg) => safeDiffNights(segFromDate(seg), segToDate(seg)),
              },
              {
                title: "Segment",
                key: "segmentPrice",
                width: 130,
                render: (_, seg) => {
                  const v = seg?.segmentPrice ?? seg?.SegmentPrice;
                  // use booking currency if we have it (invoice.currency usually present)
                  const ccy = (base?.invoice?.currency ?? base?.invoice?.Currency ?? p?.ccy ?? "BWP").toUpperCase();
                  return formatMoney(ccy, v ?? 0);
                },
              },
            ];

            const ccyUp = String(p.ccy || "BWP");
            const notes = base?.notes ?? base?.Notes;
            const remarks = base?.remarks ?? base?.Remarks;

            return (
              <>
                <div className="hms-summary">
                  <div>
                    <div className="hms-summary-title">{getGuestName(base) || "Guest"}</div>
                    <div className="hms-summary-sub">Booking {base?.bookingNo ?? base?.BookingNo ?? "—"}</div>
                  </div>
                  {getStatusTag(statusText)}
                </div>

                <DetailList
                  items={[
                    { label: "ID number", value: getIdNumber(base) },
                    {
                      label: "Room",
                      value: (
                        <>
                          {defaultRoomNumber || "—"}
                          {split && <span className="hms-pill">Split stay</span>}
                        </>
                      ),
                    },
                    { label: "Room type", value: defaultRoomType },
                    { label: "Check-in", value: fmtDate(base?.checkInDate ?? base?.CheckInDate) },
                    { label: "Check-out", value: fmtDate(base?.checkOutDate ?? base?.CheckOutDate) },
                    { label: "Nights", value: p.nights },
                  ]}
                />

                <Section
                  title="Room move plan"
                  extra={
                    <span className="hms-pill">
                      {split
                        ? `${Number(base?.roomsCount ?? base?.RoomsCount ?? segments.length) || segments.length} rooms`
                        : "Single room"}
                    </span>
                  }
                >
                  {segments.length ? (
                    <>
                      {(apiSummary || route) && (
                        <DetailList
                          items={[
                            apiSummary ? { label: "Rooms", value: apiSummary } : null,
                            route ? { label: "Route", value: route } : null,
                          ]}
                        />
                      )}

                      <Table
                        className="hms-table"
                        rowKey={(seg, idx) => seg?.roomBookingId ?? seg?.RoomBookingId ?? seg?.id ?? idx}
                        columns={segColumns}
                        dataSource={segments}
                        pagination={false}
                        scroll={{ x: 720 }}
                        locale={{ emptyText: "No room segments" }}
                      />

                      {segments.length > 1 && (
                        <Timeline
                          style={{ marginTop: 16 }}
                          items={segments.map((seg) => {
                            const from = segFromDate(seg);
                            const to = segToDate(seg);
                            return {
                              children: (
                                <div className="hms-cell-stack">
                                  <span className="hms-cell-main">
                                    {segRoomNumber(seg)} — {segRoomTypeName(seg)}
                                  </span>
                                  <span className="hms-cell-sub">
                                    {fmtDate(from)} to {fmtDate(to)}
                                  </span>
                                </div>
                              ),
                            };
                          })}
                        />
                      )}
                    </>
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message="No room segments recorded"
                      description="If this booking involves moving rooms, ensure RoomBookings/legs are saved for this BookingId."
                    />
                  )}
                </Section>

                <Section title="Price breakdown">
                  <AmountRows
                    rows={[
                      {
                        label: "Base / night",
                        value: p.discountApplied ? (
                          <>
                            <s className="hms-muted">{formatMoney(ccyUp, p.baseOriginal)}</s>{" "}
                            <strong>{formatMoney(ccyUp, p.base)}</strong>
                            <span className="hms-pill">-{p.discountPct}%</span>
                          </>
                        ) : (
                          formatMoney(ccyUp, p.base)
                        ),
                      },
                      Number(p.vatPct) > 0 ? { label: `VAT (${p.vatPct}%)`, value: formatMoney(ccyUp, p.vat) } : null,
                      { label: "Fees / night", value: formatMoney(ccyUp, p.levies) },
                      { divider: true },
                      { label: "Total / night", value: formatMoney(ccyUp, p.perNight), strong: true },
                      { label: "Nights", value: p.nights },
                      { divider: true },
                      { label: "Total", value: formatMoney(ccyUp, p.total), strong: true },
                      { label: "Paid", value: formatMoney(ccyUp, p.paid), muted: true },
                      { label: "Due", value: formatMoney(ccyUp, p.due), danger: true },
                    ]}
                  />
                  <div className="hms-summary-sub" style={{ marginTop: 8 }}>
                    Price source: {p.source}
                  </div>
                </Section>

                {(notes || remarks) && (
                  <Section title="Notes">
                    <DetailList
                      items={[
                        remarks ? { label: "Remarks", value: remarks } : null,
                        notes ? { label: "Notes", value: notes } : null,
                      ]}
                    />
                  </Section>
                )}
              </>
            );
          })()
        ) : (
          <Empty description="No booking selected" />
        )}
      </Drawer>
    </div>
  );
};

export default BookingListPage;
